import { useState, useEffect, useCallback } from 'react';
import { useDBA } from '@/contexts/DBAContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { money } from '@/components/estimator/EstimatorComponents';
import { formatPhone, formatPreferredWindow, mapsUrl } from '@/lib/formatters';

export const stages = ['new', 'contacted', 'needs_quote', 'quoted', 'won', 'lost'];
export const stageColors = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-white text-zinc-800 border-zinc-300',
  needs_quote: 'bg-amber-50 text-amber-800 border-amber-200',
  quoted: 'bg-blue-50 text-blue-700 border-blue-200',
  won: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  lost: 'bg-red-100 text-red-700 border-red-200',
};

export function useLeadsPageModel() {

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { dbas, selectedDBA, getDBAName, getDBAColor } = useDBA();
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [dispositionFilter, setDispositionFilter] = useState('active');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [showAdd, setShowAdd] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [batchAction, setBatchAction] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [quoteLineItems, setQuoteLineItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [quoteTaxRate, setQuoteTaxRate] = useState(0);
  const [leadTab, setLeadTab] = useState('overview');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [customerMatches, setCustomerMatches] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('new');
  const [conversionMode, setConversionMode] = useState({ type: 'lead', estimateId: '' });
  const [actionMessage, setActionMessage] = useState('');
  const [estimatorBundle, setEstimatorBundle] = useState(null);
  const [estimatorTab, setEstimatorTab] = useState('quick');
  const [estimatorEstimate, setEstimatorEstimate] = useState(null);
  const [savedEstimate, setSavedEstimate] = useState(null);
  const [estimatePhotos, setEstimatePhotos] = useState([]);
  const [estimatePhotoUploading, setEstimatePhotoUploading] = useState(false);
  const [estimatorMessage, setEstimatorMessage] = useState('');
  const [estimatorSaving, setEstimatorSaving] = useState(false);
  const [actuals, setActuals] = useState({ actual_labor_hours: 0, actual_workers: 2, actual_round_trip_miles: 0, actual_dump_fee: 0, actual_rental_cost: 0, actual_misc_cost: 0, actual_final_charged_price: 0, actual_notes: '' });
  const [estimatorForm, setEstimatorForm] = useState({ mode: 'quick', load_category_key: 'full_pickup', estimated_hours: 2, workers: 2, estimated_cubic_yards: 3, round_trip_miles: 15, lead_id: '', discount_amount: 0, discount_percent: 0, disposal_type: 'standard', weight_risk: 'normal', rental_vehicle_needed: false, confidence_level: 'medium', photo_notes: '', suggested_range: '', override_price: '', override_reason: '', selected_access_fee_keys: [], selected_specialty_items: [], heavy_materials: [] });

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (selectedDBA) params.dba_id = selectedDBA;
      if (stageFilter !== 'all') params.stage = stageFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (assigneeFilter !== 'all') params.assigned_to = assigneeFilter;
      if (dispositionFilter !== 'all') params.disposition = dispositionFilter;
      if (search) params.search = search;
      if (sortBy === 'value') { params.sort_by = 'value'; params.sort_dir = 'desc'; }
      else if (sortBy === 'name') { params.sort_by = 'name'; params.sort_dir = 'asc'; }
      const { data } = await api.get('/leads', { params });
      setLeads(data);
    } catch { return; }
    finally { setLoading(false); }
  }, [selectedDBA, stageFilter, sourceFilter, assigneeFilter, dispositionFilter, search, sortBy]);

  const fetchTeam = useCallback(async () => {
    try { const { data } = await api.get('/team'); setTeam(data); }
    catch { return; }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const getTeamName = (id) => team.find(t => t.id === id)?.name || '';
  const formatTeamOption = (member) => `${member.name || member.email || 'Team member'} (${(member.role || 'team').replace('_', ' ')})`;
  const quoteUrl = (quote) => `${window.location.origin}/quote/${quote?.public_token || quote?.id || quote || ''}`;
  const formatFieldName = (field) => (field || '').replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
  const leadStreetAddress = (lead) => lead?.street_address || lead?.address || '';
  const leadCityStateZip = (lead) => [lead?.city, [lead?.state, lead?.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const smsConsentOk = (lead) => !!lead?.consent?.sms_consent && !lead?.consent?.sms_opt_out_at && lead?.sms_opted_out !== true;
  const quoteDeliveryLabel = (lead) => smsConsentOk(lead) ? 'SMS + email' : 'Email only';
  const quoteSubtotal = quoteLineItems.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0), 0);
  const quoteTotal = quoteSubtotal + quoteSubtotal * ((Number(quoteTaxRate) || 0) / 100);

  const resetQuoteDraft = useCallback((lead) => {
    setQuoteLineItems([{ description: lead?.service_category || 'Service', quantity: 1, unit_price: lead?.estimated_value || 0 }]);
    setQuoteTaxRate(0);
  }, []);

  const resetEstimatorDraft = useCallback((lead) => {
    setEstimatorTab('quick');
    setEstimatorEstimate(null);
    setSavedEstimate(null);
    setEstimatePhotos([]);
    setEstimatorMessage('');
    setActuals({ actual_labor_hours: 0, actual_workers: 2, actual_round_trip_miles: 0, actual_dump_fee: 0, actual_rental_cost: 0, actual_misc_cost: 0, actual_final_charged_price: lead?.estimated_value || 0, actual_notes: '' });
    setEstimatorForm({ mode: 'quick', load_category_key: 'full_pickup', estimated_hours: 2, workers: 2, estimated_cubic_yards: 3, round_trip_miles: 15, lead_id: lead?.id || '', discount_amount: 0, discount_percent: 0, disposal_type: 'standard', weight_risk: 'normal', rental_vehicle_needed: false, confidence_level: 'medium', photo_notes: '', suggested_range: '', override_price: '', override_reason: '', selected_access_fee_keys: [], selected_specialty_items: [], heavy_materials: [] });
  }, []);

  const addQuoteLineItem = () => setQuoteLineItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0 }]);
  const updateQuoteLineItem = (index, field, value) => {
    setQuoteLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: field === 'description' ? value : parseFloat(value) || 0 } : item));
  };
  const removeQuoteLineItem = (index) => {
    setQuoteLineItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  };

  const openCreateLead = () => {
    setEditingLead(null);
    setError('');
    setShowAdd(true);
  };

  const openEditLead = (lead) => {
    setEditingLead(lead);
    setError('');
    setShowAdd(true);
  };

  const refreshLeadDetail = useCallback(async (id) => {
    const { data } = await api.get(`/leads/${id}/lifecycle`);
    const lead = data.lead || data;
    setShowDetail(lead);
    setLifecycle(data.lead ? data : { lead, quotes: [], jobs: [], invoices: [], contacts: lead.contact_history || [], timeline: [] });
    resetQuoteDraft(lead);
  }, [resetQuoteDraft]);

  const fetchEstimatorBundle = useCallback(async (lead) => {
    if (!lead?.dba_id) return;
    try {
      const { data } = await api.get('/estimator/config', { params: { dba_id: lead.dba_id } });
      setEstimatorBundle(data);
    } catch (err) {
      setEstimatorMessage(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const openLeadDetail = useCallback(async (lead) => {
    setLeadTab('overview');
    setActionMessage('');
    resetEstimatorDraft(lead);
    setShowDetail(lead);
    fetchEstimatorBundle(lead);
    await refreshLeadDetail(lead.id);
  }, [fetchEstimatorBundle, refreshLeadDetail, resetEstimatorDraft]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const leadIdFromUrl = params.get('openLeadId') || params.get('leadId') || '';
    const requestedLeadId = location.state?.openLeadId || leadIdFromUrl;
    if (!requestedLeadId) return;
    const openFromState = async () => {
      try {
        const { data } = await api.get(`/leads/${requestedLeadId}`);
        await openLeadDetail(data);
        navigate('/leads', { replace: true });
      } catch { return; }
    };
    openFromState();
  }, [location.state, location.search, navigate, openLeadDetail]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    const f = new FormData(e.target);
    try {
      const payload = {
        contact_name: f.get('contact_name') || [f.get('first_name'), f.get('last_name')].filter(Boolean).join(' '),
        first_name: f.get('first_name') || '',
        last_name: f.get('last_name') || '',
        contact_email: f.get('contact_email'),
        contact_phone: f.get('contact_phone'),
        address: f.get('address'),
        city: f.get('city') || '',
        state: f.get('state') || '',
        zip_code: f.get('zip_code') || '',
        dba_id: f.get('dba_id'),
        service_category: f.get('service_category'),
        estimated_value: parseFloat(f.get('estimated_value') || '0'),
        source: f.get('source'),
        source_type: f.get('source_type') || 'organic',
        source_platform: f.get('source_platform') || '',
        lead_cost: parseFloat(f.get('lead_cost') || '0'),
        notes: f.get('notes'),
        preferred_date: f.get('preferred_date') || '',
        preferred_time: f.get('preferred_time') || '',
        preferred_time_frame: f.get('preferred_time_frame') || '',
      };
      if (editingLead?.id) {
        await api.put(`/leads/${editingLead.id}`, {
          ...payload,
          assigned_to: editingLead.assigned_to || '',
          lifecycle_stage: editingLead.lifecycle_stage || editingLead.stage || 'new',
          edit_comment: f.get('edit_comment') || '',
        });
      } else {
        await api.post('/leads', payload);
      }
      setShowAdd(false);
      const editedId = editingLead?.id;
      setEditingLead(null);
      await fetchLeads();
      if (editedId && showDetail?.id === editedId) {
        await refreshLeadDetail(editedId);
      }
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const updateStage = async (id, stage) => {
    await api.put(`/leads/${id}/stage`, { stage });
    fetchLeads();
    if (showDetail?.id === id) {
      await refreshLeadDetail(id);
    }
  };

  const assignLead = async (id, userId) => {
    await api.put(`/leads/${id}`, { assigned_to: userId });
    fetchLeads();
    if (showDetail?.id === id) {
      await refreshLeadDetail(id);
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !showDetail) return;
    await api.post(`/leads/${showDetail.id}/notes`, { text: noteText });
    setNoteText('');
    await refreshLeadDetail(showDetail.id);
    fetchLeads();
  };

  const logContact = async (e) => {
    e.preventDefault();
    if (!showDetail) return;
    const f = new FormData(e.target);
    await api.post(`/leads/${showDetail.id}/contact`, {
      method: f.get('method'),
      outcome: f.get('outcome'),
      contacted_at: f.get('contacted_at') ? new Date(f.get('contacted_at')).toISOString() : '',
      notes: f.get('notes'),
      next_follow_up_at: f.get('next_follow_up_at') ? new Date(f.get('next_follow_up_at')).toISOString() : '',
    });
    e.target.reset();
    await refreshLeadDetail(showDetail.id);
    fetchLeads();
  };

  const createQuoteFromLead = async (e) => {
    e.preventDefault();
    if (!showDetail) return;
    const f = new FormData(e.target);
    await api.post(`/leads/${showDetail.id}/quote`, {
      status: 'sent',
      line_items: quoteLineItems.filter(item => item.description).map(item => ({
        description: item.description,
        quantity: parseFloat(item.quantity || '1'),
        unit_price: parseFloat(item.unit_price || '0'),
      })),
      tax_rate: quoteTaxRate,
      notes: f.get('notes') || '',
      valid_days: parseInt(f.get('valid_days') || '30', 10),
    });
    await refreshLeadDetail(showDetail.id);
    fetchLeads();
  };

  const updateEstimator = (key, value) => setEstimatorForm(prev => ({ ...prev, [key]: value }));
  const selectedCategory = estimatorBundle?.load_categories?.find(item => item.key === estimatorForm.load_category_key);
  const estimatorPayload = () => ({ ...estimatorForm, lead_id: showDetail?.id || estimatorForm.lead_id, dba_id: showDetail?.dba_id, mode: estimatorTab, rental_vehicle_needed: selectedCategory?.requires_uhaul || estimatorForm.rental_vehicle_needed });
  const toggleAccess = (key) => setEstimatorForm(prev => ({ ...prev, selected_access_fee_keys: prev.selected_access_fee_keys.includes(key) ? prev.selected_access_fee_keys.filter(k => k !== key) : [...prev.selected_access_fee_keys, key] }));
  const setSpecialtyQty = (key, qty) => setEstimatorForm(prev => ({ ...prev, selected_specialty_items: qty > 0 ? [...prev.selected_specialty_items.filter(i => i.key !== key), { key, quantity: qty }] : prev.selected_specialty_items.filter(i => i.key !== key) }));
  const addHeavy = () => setEstimatorForm(prev => ({ ...prev, heavy_materials: [...prev.heavy_materials, { material: 'Concrete', estimated_tons: 0, disposal_fee_per_ton: 85, multiplier: 3 }] }));
  const updateHeavy = (index, key, value) => setEstimatorForm(prev => ({ ...prev, heavy_materials: prev.heavy_materials.map((item, i) => i === index ? { ...item, [key]: key === 'material' ? value : parseFloat(value) || 0 } : item) }));

  const calculateEmbeddedEstimate = async () => {
    setEstimatorMessage('');
    const { data } = await api.post('/estimator/calculate', estimatorPayload());
    setEstimatorEstimate(data.calculation);
    setSavedEstimate(null);
  };

  const saveEmbeddedEstimate = async () => {
    setEstimatorSaving(true);
    setEstimatorMessage('');
    try {
      const { data } = await api.post('/estimator/estimates', estimatorPayload());
      setSavedEstimate(data);
      setEstimatorEstimate(data.calculation);
      setShowDetail(prev => prev ? { ...prev, last_estimate_id: data.id, last_estimate_number: data.estimate_number, last_estimate_total: data.calculation?.final_price } : prev);
      setActuals(prev => ({ ...prev, actual_final_charged_price: data.calculation.final_price }));
      setEstimatorMessage(`Saved ${data.estimate_number} to this lead.`);
      await refreshLeadDetail(showDetail.id);
      await fetchLeads();
    } catch (err) {
      setEstimatorMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setEstimatorSaving(false);
    }
  };

  const createQuoteFromEstimate = async () => {
    setEstimatorSaving(true);
    setEstimatorMessage('');
    try {
      let estimateForQuote = savedEstimate;
      if (!estimateForQuote?.id) {
        const { data } = await api.post('/estimator/estimates', estimatorPayload());
        estimateForQuote = data;
        setSavedEstimate(data);
        setShowDetail(prev => prev ? { ...prev, last_estimate_id: data.id, last_estimate_number: data.estimate_number, last_estimate_total: data.calculation?.final_price } : prev);
        setEstimatorEstimate(data.calculation);
        setActuals(prev => ({ ...prev, actual_final_charged_price: data.calculation.final_price }));
      }
      const { data } = await api.post(`/estimator/estimates/${estimateForQuote.id}/quote`);
      const smsStatus = data.notification_result?.sms?.status || data.sms_result?.status;
      const emailStatus = data.notification_result?.email?.status || data.email_result?.status;
      const smsNote = smsStatus === 'skipped' ? ' SMS skipped; email used.' : smsStatus === 'disabled' ? ' SMS logged but automation sending is disabled.' : smsStatus ? ' Acceptance text sent.' : '';
      const emailNote = emailStatus === 'skipped' ? ' Email skipped because no address is on file.' : emailStatus === 'disabled' ? ' Email logged but sending is disabled.' : emailStatus ? ' Email sent/logged.' : '';
      setEstimatorMessage(`Quote created: ${data.quote_number}.${smsNote}${emailNote}`);
      await refreshLeadDetail(showDetail.id);
      await fetchLeads();
    } catch (err) {
      setEstimatorMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setEstimatorSaving(false);
    }
  };

  const saveEstimateForPhoto = async () => {
    if (savedEstimate?.id) return savedEstimate;
    const { data } = await api.post('/estimator/estimates', estimatorPayload());
    setSavedEstimate(data);
    setEstimatorEstimate(data.calculation);
    setShowDetail(prev => prev ? { ...prev, last_estimate_id: data.id, last_estimate_number: data.estimate_number, last_estimate_total: data.calculation?.final_price } : prev);
    return data;
  };

  const uploadEmbeddedEstimatePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setEstimatePhotoUploading(true);
    setEstimatorMessage('');
    try {
      const estimateForPhoto = await saveEstimateForPhoto();
      const data = new FormData();
      data.append('file', file);
      const { data: photo } = await api.post('/photos/upload', data, { params: { entity_type: 'estimate', entity_id: estimateForPhoto.id, photo_stage: 'estimate' }, headers: { 'Content-Type': 'multipart/form-data' } });
      setEstimatePhotos(prev => [photo, ...prev]);
      setEstimatorMessage('Photo attached to this estimate and lead.');
      await refreshLeadDetail(showDetail.id);
    } catch (err) {
      setEstimatorMessage(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setEstimatePhotoUploading(false);
    }
  };

  const acceptEmbeddedEstimate = async (estimateId, customerId = '') => {
    setEstimatorSaving(true);
    setEstimatorMessage('');
    try {
      const { data } = await api.post(`/estimator/estimates/${estimateId}/accept`, { customer_id: customerId });
      setSavedEstimate(data.estimate);
      setEstimatorEstimate(data.estimate?.calculation || estimatorEstimate);
      setEstimatorMessage('Estimate accepted and lead converted to customer.');
      await refreshLeadDetail(showDetail.id);
      await fetchLeads();
      if (data.conversion?.customer_id) navigate(`/customers/${data.conversion.customer_id}`);
    } catch (err) {
      setEstimatorMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setEstimatorSaving(false);
    }
  };

  const saveEmbeddedActuals = async () => {
    if (!savedEstimate?.id) return setEstimatorMessage('Save or select an estimate before recording actuals.');
    setEstimatorSaving(true);
    try {
      const { data } = await api.post(`/estimator/estimates/${savedEstimate.id}/actuals`, actuals);
      setSavedEstimate(data);
      setEstimatorMessage(`Actual profit: ${money(data.actuals?.actual_gross_profit)} · ${data.actuals?.actual_profit_margin}% margin`);
      await refreshLeadDetail(showDetail.id);
    } catch (err) {
      setEstimatorMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setEstimatorSaving(false);
    }
  };

  const convertLeadToCustomer = async (customerId = '') => {
    if (!showDetail) return;
    try {
      const { data } = await api.post(`/leads/${showDetail.id}/convert-customer`, { source: 'manual_conversion', customer_id: customerId, comment: customerId ? 'Manual conversion linked to existing customer' : 'Manual conversion from Lead Workspace' });
      setActionMessage('Lead converted to customer.');
      setConvertDialogOpen(false);
      await refreshLeadDetail(showDetail.id);
      await fetchLeads();
      if (data.customer_id) navigate(`/customers/${data.customer_id}`);
    } catch (err) {
      setActionMessage(formatApiError(err.response?.data?.detail));
    }
  };

  const openConversionPicker = async (type = 'lead', estimateId = '') => {
    if (!showDetail) return;
    setActionMessage('');
    setEstimatorMessage('');
    setConversionMode({ type, estimateId });
    setSelectedCustomerId('new');
    setCustomerMatches([]);
    try {
      const { data } = await api.get(`/leads/${showDetail.id}/customer-matches`);
      setCustomerMatches(data.matches || []);
      if (data.matches?.length) setSelectedCustomerId(data.matches[0].id);
    } catch (err) {
      setActionMessage(formatApiError(err.response?.data?.detail));
    }
    setConvertDialogOpen(true);
  };

  const confirmConversionPicker = async () => {
    const customerId = selectedCustomerId === 'new' ? '' : selectedCustomerId;
    if (conversionMode.type === 'estimate') {
      await acceptEmbeddedEstimate(conversionMode.estimateId, customerId);
      setConvertDialogOpen(false);
      return;
    }
    await convertLeadToCustomer(customerId);
  };

  const archiveLead = async () => {
    if (!showDetail) return;
    try {
      const { data } = await api.post(`/leads/${showDetail.id}/archive`, { reason: archiveReason });
      setArchiveDialogOpen(false);
      setArchiveReason('');
      setShowDetail(data);
      setActionMessage('Lead moved to archived view.');
      await fetchLeads();
    } catch (err) {
      setActionMessage(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteLead = async () => {
    if (!showDetail) return;
    try {
      await api.delete(`/leads/${showDetail.id}`, { data: { confirm: deleteConfirm, reason: deleteReason } });
      setDeleteDialogOpen(false);
      setDeleteReason('');
      setDeleteConfirm('');
      setShowDetail(null);
      setLifecycle(null);
      setActionMessage('Lead permanently deleted.');
      await fetchLeads();
    } catch (err) {
      setActionMessage(formatApiError(err.response?.data?.detail));
    }
  };

  const restoreLead = async () => {
    if (!showDetail) return;
    try {
      await api.post(`/leads/${showDetail.id}/restore`, {});
      setActionMessage('Lead restored to active workspace.');
      await refreshLeadDetail(showDetail.id);
      await fetchLeads();
    } catch (err) {
      setActionMessage(formatApiError(err.response?.data?.detail));
    }
  };

  const convertToJob = async () => {
    if (!showDetail) return;
    try {
      await api.post(`/leads/${showDetail.id}/convert`, { title: `Job for ${showDetail.contact_name}`, status: 'tentative' });
      await refreshLeadDetail(showDetail.id);
      fetchLeads();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const convertQuoteToJob = async (quoteId) => {
    if (!quoteId) return;
    try {
      await api.post(`/quotes/${quoteId}/convert`, { title: `Job for ${showDetail?.contact_name || 'accepted quote'}`, status: 'tentative' });
      await refreshLeadDetail(showDetail.id);
      fetchLeads();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const bookLead = async (status = 'tentative') => {
    if (!showDetail) return;
    try {
      await api.post(`/leads/${showDetail.id}/book`, {
        scheduled_date: showDetail.preferred_date || '',
        scheduled_time_start: showDetail.preferred_time || '',
        assigned_staff: [],
        status,
        title: `${showDetail.service_category || 'Service'} - ${showDetail.contact_name}`,
      });
      await refreshLeadDetail(showDetail.id);
      fetchLeads();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  // Batch operations
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map(l => l.id)));
  };
  const clearSelection = () => { setSelected(new Set()); setBatchAction(null); };

  const batchStage = async (stage) => {
    await api.post('/leads/batch/stage', { lead_ids: [...selected], stage });
    clearSelection();
    fetchLeads();
  };
  const batchAssign = async (userId) => {
    await api.post('/leads/batch/assign', { lead_ids: [...selected], assigned_to: userId });
    clearSelection();
    fetchLeads();
  };

  const stageCounts = stages.reduce((acc, s) => { acc[s] = leads.filter(l => l.stage === s).length; return acc; }, {});
  const leadPermissions = user?.permissions?.leads || [];
  const canCreateLead = user?.role === 'admin' || leadPermissions.includes('create') || leadPermissions.includes('manage');
  const canEditLead = user?.role === 'admin' || leadPermissions.includes('edit') || leadPermissions.includes('assign') || leadPermissions.includes('manage');

  return {
    data: {
      leads, team, loading, search, stageFilter, dispositionFilter, sourceFilter, assigneeFilter, sortBy,
      showAdd, editingLead, showDetail, noteText, error, selected, lifecycle, quoteLineItems, quoteTaxRate,
      quoteSubtotal, quoteTotal, leadTab, archiveDialogOpen, archiveReason, deleteDialogOpen, deleteReason,
      deleteConfirm, convertDialogOpen, customerMatches, selectedCustomerId, conversionMode, actionMessage,
      estimatorBundle, estimatorTab, estimatorEstimate, savedEstimate, estimatePhotos, estimatePhotoUploading,
      estimatorMessage, estimatorSaving, actuals, estimatorForm, stageCounts, leadPermissions, canCreateLead,
      canEditLead, dbas, selectedDBA, user,
    },
    actions: {
      setSearch, setStageFilter, setDispositionFilter, setSourceFilter, setAssigneeFilter, setSortBy,
      setShowAdd, setEditingLead, setShowDetail, setLifecycle, setNoteText, setLeadTab, setArchiveDialogOpen,
      setArchiveReason, setDeleteDialogOpen, setDeleteReason, setDeleteConfirm, setConvertDialogOpen,
      setSelectedCustomerId, handleCreate, openCreateLead, openEditLead, openLeadDetail, restoreLead,
      openConversionPicker, updateStage, assignLead, logContact, addNote, setEstimatorTab, updateEstimator,
      toggleAccess, setSpecialtyQty, addHeavy, updateHeavy, calculateEmbeddedEstimate, saveEmbeddedEstimate,
      createQuoteFromEstimate, setActuals, saveEmbeddedActuals, uploadEmbeddedEstimatePhoto, setSavedEstimate,
      setEstimatorEstimate, createQuoteFromLead, addQuoteLineItem, updateQuoteLineItem, removeQuoteLineItem,
      setQuoteTaxRate, convertToJob, convertQuoteToJob, bookLead, archiveLead, deleteLead,
      confirmConversionPicker, batchStage, batchAssign, clearSelection, toggleSelectAll, toggleSelect,
      navigate,
    },
    helpers: {
      getDBAName, getDBAColor, getTeamName, formatTeamOption, smsConsentOk, quoteDeliveryLabel, formatPhone,
      mapsUrl, leadCityStateZip, formatPreferredWindow, formatFieldName, quoteUrl, leadStreetAddress,
    },
  };
}
